/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */
import { useRef, type ComponentType } from "react";
import { mergeProps, useButton, useFocusRing, useHover } from "react-aria";
import { cssFns, useCss } from "@superweb/css";
import { useUiColors } from "../theme";
import { useTypo } from "../typo";
import { Button } from "../buttons/button";

export const ActionButton = ({
  label,
  ariaLabel,
  icon,
  variant,
  onPress,
}: {
  label?: string;
  ariaLabel?: string;
  icon?: ComponentType<{ className?: string }>;
  variant: "outlineInvert" | "contrastOutline";
  onPress?: () => void;
}) => {
  return variant === "outlineInvert" ? (
    <OutlineInvertButton
      label={label}
      ariaLabel={ariaLabel}
      icon={icon}
      onPress={onPress}
    />
  ) : (
    <Button
      text={label}
      ariaLabel={ariaLabel}
      icon={icon}
      variant="contrastOutline"
      size="xs"
      onPress={onPress}
    />
  );
};

const OutlineInvertButton = ({
  label,
  ariaLabel,
  icon: Icon,
  onPress,
}: {
  label?: string;
  ariaLabel?: string;
  icon?: ComponentType<{ className?: string }>;
  onPress?: () => void;
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  const { buttonProps, isPressed } = useButton(
    {
      "aria-label": ariaLabel,
      onPress,
    },
    ref,
  );
  const { hoverProps, isHovered } = useHover({});
  const { focusProps, isFocusVisible: isFocused } = useFocusRing();
  const css = useCss();
  const typo = useTypo();
  const uiColors = useUiColors();
  const transparent = cssFns.transparentize(uiColors.everBack, 1);
  const backgroundColor = isPressed
    ? cssFns.blend(transparent, uiColors.press)
    : isHovered
      ? cssFns.blend(transparent, uiColors.hover)
      : transparent;
  return (
    <button
      {...mergeProps(hoverProps, focusProps, buttonProps)}
      ref={ref}
      css={{
        // Button element cannot be reliably styled in Safari < 14.
        // Hence we use it only as a semantic container with reset styling
        // and mark up the inner layout using span.
        appearance: "none",
        ...cssFns.margin("0"),
        ...cssFns.padding("0"),
        ...cssFns.border({ style: "none" }),
        outlineStyle: "none",
        backgroundColor: "transparent",
      }}
    >
      <span
        css={{
          height: "32px",
          display: "grid",
          gridTemplateColumns: label && Icon ? "auto auto" : "auto",
          ...cssFns.gap("4px"),
          alignContent: "center",
          justifyContent: "center",
          alignItems: "center",
          boxSizing: "border-box",
          paddingInlineStart: Icon ? "6px" : "12px",
          paddingInlineEnd: label ? "12px" : "6px",
          ...cssFns.border({
            width: "0.5px",
            style: "solid",
            color: uiColors.textInvert,
            radius: "10px",
          }),
          ...cssFns.overflow("hidden"),
          boxShadow: `0 0 0 0 transparent, ${
            isFocused
              ? "inset 0 0 0 2px " + uiColors.focus
              : "0 0 0 0 transparent"
          }`,
          cursor: "pointer",
          backgroundColor,
          color: uiColors.textInvert,
          outlineStyle: "none",
        }}
      >
        {Icon && (
          <Icon
            className={css({
              width: "20px",
              height: "20px",
            })}
          />
        )}
        {label && (
          <span
            css={{
              ...typo({
                level: "caption1",
                weight: "regular",
                density: "tight",
              }),
              ...cssFns.overflow("hidden"),
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
        )}
      </span>
    </button>
  );
};
